import { useEffect, useState } from "react";
import type { SearchQuery, VehicleCard } from "../types/vtc";
import { CATEGORY_TILES } from "../data/home";
import { getHome } from "../lib/marketplaceApi";
import type { HomeCategory } from "../lib/marketplaceApi";
import { useT, useLang } from "../i18n";

import { organizationLd, useSeo, websiteLd } from "../lib/seo";
import Header from "../components/layout/Header";
import Hero from "../components/home/Hero";
import SearchBar from "../components/home/SearchBar";
import PopularVehicles from "../components/home/PopularVehicles";
import CategoryTiles from "../components/home/CategoryTiles";
import HowItWorks from "../components/home/HowItWorks";
import PartnerCta from "../components/home/PartnerCta";
import heroImage from "@/assets/hero.webp";
import Footer from "@/components/layout/Footer";
export default function Home() {
  const t = useT();
  const lang = useLang();
  useSeo({
    title: t.seo.home.title,
    fullTitle: true,
    description: t.seo.home.description,
    ogTitle: t.seo.home.ogTitle,
    ogDescription: t.seo.home.ogDescription,
    path: "/",
    jsonLd: [organizationLd(t.seo.home.description), websiteLd(lang)],
  });
  // GET /listings/home: published listings of verified companies, sponsored first
  const [vehicles, setVehicles] = useState<VehicleCard[] | null>(null);
  const [categories, setCategories] = useState<HomeCategory[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    getHome()
      .then((home) => {
        setVehicles(home.popular);
        setCategories(home.categories);
      })
      .catch(() => {
        setVehicles([]);
        setError(true);
      });
  }, []);

  const handleSearch = (query: SearchQuery) => {
    // No city = every location
    const params = new URLSearchParams({
      ...(query.city && { city: query.city }),
      start: query.start,
      end: query.end,
    });
    window.location.href = `/vehicles?${params.toString()}`;
  };

  const handleCategory = (filter: string) => {
    window.location.href = `/vehicles?category=${encodeURIComponent(filter)}`;
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/" />

      <main className="flex-1">
        <Hero  imageUrl={heroImage}/>
        <SearchBar onSearch={handleSearch} />
        <PopularVehicles vehicles={vehicles ?? []} loading={vehicles === null} error={error} />
        <CategoryTiles
          categories={CATEGORY_TILES}
          brands={Object.fromEntries(categories.map((c) => [c.id, c.brands]))}
          onSelect={handleCategory}
        />
        <HowItWorks steps={t.home.steps} />
        <PartnerCta benefits={t.home.partner.benefits} />
      </main>
      <Footer />
    </div>
  );
}
